<template>
  <ListLayout
    :title="`${tripPlanningModule.tagPlural}`"
    :context="context"
    :tableData="itemList"
    :tableHeader="tableHeaders"
    :total="totalItems"
    @getList="getTagList"
  >
    <template #listAction>
      <v-menu :offset-y="true" left>
        <template v-slot:activator="{ on, attrs }">
          <SecondaryButton
            id="tagsExport"
            v-bind="attrs"
            btnClass="mr-2 rounded-lg"
            iconClass="mr-2 secondary--text"
            iconSize="lg"
            outlined
            v-on="on"
            icon="mdi-download"
            name="Export"
          />
        </template>
        <v-list dense flat>
          <v-list-item
            @click="
              showDownloadSheetDialog(
                'Export Vehicle Tags',
                'vehicleTags',
                'downloadVehicleTagsSheet'
              )
            "
          >
            <v-list-item-title
              >Export {{ fleetModule.vehicle }}
              {{ tripPlanningModule.tagPlural }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item
            @click="
              showDownloadSheetDialog(
                'Export Customer Address Tags Sheet',
                'customerAddressTags',
                'downloadCustomerAddressTagsSheet'
              )
            "
          >
            <v-list-item-title
              >Export {{ customerAddressModule.customerAddress }}
              {{ tripPlanningModule.tagPlural }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item @click="getExportData">
            <v-list-item-title
              >Export All {{ tripPlanningModule.tagPlural }}</v-list-item-title
            >
          </v-list-item>
        </v-list>
      </v-menu>

      <v-menu offset-y left v-if="permissions && permissions.add">
        <template v-slot:activator="{ on, attrs }">
          <PrimaryButton
            id="tagsAdd"
            v-bind="attrs"
            v-on="on"
            v-shortkey="['ctrl', 'shift', 'a']"
            @shortkey="addTag"
            :name="`Add ${tripPlanningModule.tag}`"
            icon="mdi-plus"
            iconSize="x-large"
          >
          </PrimaryButton>
        </template>
        <v-list dense flat>
          <v-list-item id="add-tag" @click="addTag">
            <v-list-item-title
              >Add {{ tripPlanningModule.tag }}</v-list-item-title
            >
          </v-list-item>
          <v-list-item id="import-customer-tags" @click="bulkUploadTags('customer_tag')">
            <v-list-item-title
              >Import {{ customerModule.customer }}
              {{ tripPlanningModule.tagPlural }}</v-list-item-title
            >
          </v-list-item>
          <v-list-item id="import-vehicle-tags" @click="bulkUploadTags('vehicle_tag')">
            <v-list-item-title
              >Import {{ fleetModule.vehicle }}
              {{ tripPlanningModule.tagPlural }}</v-list-item-title
            >
          </v-list-item>
        </v-list>
      </v-menu>
    </template>
    <template #dialogs>
      <TagForm
        :editMode="isEditing"
        :tagID="selectedTagID"
        v-model="showTagForm"
        @refreshList="getTagList({ limit: 10, offset: 0 })"
      />
      <BulkUpload
        :uploadTo="bulkUploadType"
        v-model="showBulkUploadDialog"
        @refreshList="getTagList({ limit: 10, offset: 0 })"
      />
      <DirectAssignTag
        v-model="showDirectAssignTagDialog"
        :tagId="selectedTag"
      />
      <DownloadSheet
        v-model="openDownloadDialog"
        :title="downloadTitle"
        :module="downloadModule"
        :method="downloadMethod"
      />
    </template>
  </ListLayout>
</template>

<script>
/* eslint-disable vue/no-unused-components */
import { bus } from "@/main.js";
import TagButton from "@/components/Button/TagButton.vue";
import ListLayout from "@/components/LayoutComponents/ListLayout.vue";
import TagForm from "@/components/ModuleBasedComponents/Tag/TagForm.vue";
import BulkUpload from "@/components/BaseComponents/BulkUpload.vue";
import DirectAssignTag from "@/components/ModuleBasedComponents/Tag/DirectAssignTag.vue";
import DownloadSheet from "@/components/GeneralComponents/DownloadSheet.vue";
import { downloadBlob, returnToday } from "@/utils/functions";
import {
  tripPlanningModule,
  customerAddressModule,
  fleetModule,
  customerModule,
} from "@/utils/moduleMapping";

export default {
  components: {
    TagButton,
    ListLayout,
    TagForm,
    BulkUpload,
    DirectAssignTag,
    DownloadSheet,
  },
  data() {
    return {
      tripPlanningModule,
      customerAddressModule,
      fleetModule,
      customerModule,
      permissions: null,
      sorting: {},
      openDownloadDialog: false,
      itemsPerPage: 10,
      pageNo: 1,
      totalItems: 0,
      filters: this.$helper.getFilterFromLocalStorage("tagFilters"),
      isChipsVisible: false,
      itemList: [],
      headerSelected: [
        {
          headerName: "Name",
          field: "name",
          sortable: true,
          pinned: "left",
          minWidth: 200,
        },
        {
          headerName: "description",
          field: "description",
          sortable: true,
          tooltipField: "description",
        },
      ],

      isEditing: false,
      showTagForm: false,
      selectedTagID: null,

      showBulkUploadDialog: false,
      bulkUploadType: "customer_tag",

      showDirectAssignTagDialog: false,
      selectedTag: null,

      downloadModule: null,
      downloadMethod: null,
      downloadTitle: null,
      openDownloadDialog: false,
    };
  },
  computed: {
    tableHeaders() {
      return [
        ...this.headerSelected,
        {
          headerName: "Actions",
          field: "actions",
          pinned: "right",
          sortable: false,
          minWidth: 100,
          width: 150,
          cellRenderer: "TagButton",
        },
      ];
    },
    context() {
      return { parentComponent: this };
    },
  },
  methods: {
    getExportData(params = {}) {
      this.$api.tag
        .downloadTagsSheet()
        .then((res) => {
          if (res && res.data) {
            downloadBlob(res.data, `${returnToday()} ${this.title}`, ".xlsx");
          }
        })
        .catch(() => {});
    },
    showDownloadSheetDialog(title, module, method) {
      this.downloadModule = module;
      this.downloadMethod = method;
      this.downloadTitle = title;
      this.openDownloadDialog = true;
    },
    bulkUploadTags(uploadType) {
      this.bulkUploadType = uploadType;
      this.showBulkUploadDialog = true;
    },
    getTagList(params = {}) {
      bus.$emit("showLoader", true);
      params = {
        ...params,
        ...this.filters,
      };

      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }

      this.isChipsVisible = Object.keys(this.filters).length > 0;

      this.$api.tag
        .getTagList(params)
        .then((res) => {
          this.itemList = res.data;
          this.totalItems = res.count;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.log(err);
          bus.$emit("showLoader", false);
        });
    },
    addTag() {
      this.selectedTagID = null;
      this.isEditing = false;
      this.showTagForm = true;
    },
    editTag(id) {
      this.selectedTagID = id;
      this.isEditing = true;
      this.showTagForm = true;
    },
    assignTagDirectly(id) {
      this.selectedTag = id;
      this.showDirectAssignTagDialog = true;
    },
    deleteTag(id) {
      bus.$emit("showLoader", false);
      this.$api.tag
        .deleteTagObject(id)
        .then(() => {
          this.getTagList();
          bus.$emit("showLoader", false);
          bus.$emit("showToastMessage", {
            message: "Tag deleted successfully!",
            color: "success",
          });
        })
        .catch((err) => {
          console.log(err);
          bus.$emit("showToastMessage", {
            message: "Couldn't delete Tag!",
            color: "error",
          });
          bus.$emit("showLoader", false);
        });
    },
  },
  created() {
    this.permissions = this.$permissions.getPermission("trip_planning");
  },
};
</script>
